import { useEffect, useRef } from 'react'
import Header from "@/components/Header"

export default function Privacy() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Reduzido o número de partículas
    const particles: Array<{x: number, y: number, size: number, speedX: number, speedY: number, opacity: number}> = []
    const particleCount = window.innerWidth < 768 ? 15 : 25 // Menos partículas no mobile

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() * 1.5 - 0.75) * (window.innerWidth < 768 ? 0.3 : 1), // 70% mais lento no mobile
        speedY: (Math.random() * 1.5 - 0.75) * (window.innerWidth < 768 ? 0.3 : 1), // 70% mais lento no mobile
        opacity: Math.random() * 0.3 + 0.1
      })
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      particles.forEach((particle, i) => {
        particle.x += particle.speedX
        particle.y += particle.speedY

        if (particle.x < 0) particle.x = canvas.width
        if (particle.x > canvas.width) particle.x = 0
        if (particle.y < 0) particle.y = canvas.height
        if (particle.y > canvas.height) particle.y = 0

        ctx.beginPath()
        ctx.fillStyle = `rgba(37, 99, 235, ${particle.opacity})`
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2)
        ctx.fill()

        // Conectar partículas próximas
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j]
          const distance = Math.sqrt(Math.pow(particle.x - p2.x, 2) + Math.pow(particle.y - p2.y, 2))
          
          if (distance < 100) {
            ctx.beginPath()
            ctx.strokeStyle = `rgba(37, 99, 235, ${0.1 * (1 - distance / 100)})`
            ctx.lineWidth = 0.5
            ctx.moveTo(particle.x, particle.y)
            ctx.lineTo(p2.x, p2.y)
            ctx.stroke()
          }
        }
      })

      requestAnimationFrame(animate)
    }

    animate()

    return () => window.removeEventListener('resize', resizeCanvas)
  }, [])

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Gradientes animados em camadas */}
      <div className="absolute inset-0">
        {/* Gradiente base */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        
        {/* Gradiente radial pulsante */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent sm:animate-pulse-slow animate-pulse-slower" />
        
        {/* Gradiente diagonal animado */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 sm:animate-gradient-slow animate-gradient-slower" />
        
        {/* Vinheta suave nas bordas */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_50%,_rgba(0,0,0,0.1)_100%)]" />
      </div>
      
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: 0.3 }}
      />

      <div className="relative">
        <Header showBackButton />

        <div className="container mx-auto px-4 py-16">
          <div className="text-center mb-16 pt-16">
            <h1 className="text-4xl font-bold mb-4">Política de Privacidade</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Como protegemos seus dados e informações no Simples.ECG
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-8">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-semibold mb-2">1. Coleta de Dados</h2>
                <p className="text-muted-foreground">
                  Coletamos apenas as informações necessárias para fornecer nossos serviços, incluindo:
                  <br />- Nome e informações de contato
                  <br />- Estado de atuação
                  <br />- Imagens de eletrocardiogramas enviadas
                  <br />- Dados de uso do sistema
                  <br />- Feedback e avaliações
                  <br />
                  Estas informações são utilizadas para melhorar nossos serviços e podem ser compartilhadas de forma anônima com a Simples.Health para desenvolvimento de produtos.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">2. Uso das Informações</h2>
                <p className="text-muted-foreground">
                  As informações coletadas são utilizadas para:
                  <br />- Fornecer análises e interpretações de eletrocardiogramas
                  <br />- Melhorar nossos serviços e algoritmos
                  <br />- Garantir a segurança da sua conta
                  <br />- Comunicar atualizações importantes
                  <br />- Enviar informações sobre produtos e serviços da Simples.Health
                  <br />- Desenvolver novos recursos e produtos
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">3. Proteção de Dados</h2>
                <p className="text-muted-foreground">
                  Implementamos medidas técnicas e organizacionais para proteger suas informações:
                  <br />- Criptografia de ponta a ponta para todas as transmissões
                  <br />- Anonimização automática de dados sensíveis nas imagens
                  <br />- Armazenamento seguro em servidores com certificações de segurança
                  <br />- Controles de acesso rigorosos
                  <br />- Auditorias regulares de segurança
                  <br />- Conformidade com a LGPD e regulamentações específicas da área de saúde
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">4. Compartilhamento de Dados</h2>
                <p className="text-muted-foreground">
                  Não vendemos, alugamos ou divulgamos informações pessoais a terceiros, exceto:
                  <br />- Quando necessário para fornecer os serviços (processamento de imagens)
                  <br />- Conforme exigido por lei ou regulamentações aplicáveis
                  <br />- Para proteger nossos direitos ou propriedade
                  <br />- Em caso de reestruturação empresarial (fusão, aquisição)
                  <br />
                  Dados anonimizados podem ser utilizados para pesquisa e desenvolvimento de IA.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">5. Seus Direitos</h2>
                <p className="text-muted-foreground">
                  De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem direito a:
                  <br />- Acessar seus dados pessoais
                  <br />- Corrigir informações imprecisas
                  <br />- Solicitar a exclusão de seus dados
                  <br />- Restringir o processamento em certas circunstâncias
                  <br />- Receber seus dados em formato portável
                  <br />- Retirar o consentimento a qualquer momento
                  <br />
                  Para exercer esses direitos, entre em contato conosco através de privacy@simples.health
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">6. Retenção de Dados</h2>
                <p className="text-muted-foreground">
                  Mantemos seus dados apenas pelo tempo necessário para:
                  <br />- Fornecer nossos serviços
                  <br />- Cumprir obrigações legais
                  <br />- Resolver disputas
                  <br />- Fazer cumprir nossos acordos
                  <br />
                  As imagens de eletrocardiogramas são mantidas por períodos específicos conforme regulamentações médicas aplicáveis.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">7. Uso de Cookies</h2>
                <p className="text-muted-foreground">
                  Utilizamos cookies e tecnologias semelhantes para melhorar a experiência do usuário, analisar o uso do site e personalizar conteúdo.
                  Você pode configurar seu navegador para recusar cookies, mas isso pode afetar algumas funcionalidades do serviço.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-semibold mb-2">8. Alterações na Política de Privacidade</h2>
                <p className="text-muted-foreground">
                  Podemos atualizar esta política periodicamente. Notificaremos você sobre alterações significativas por e-mail ou através de um aviso em nosso site.
                  Recomendamos que revise regularmente nossa política de privacidade.
                </p>
              </div>
            </div>

            <div className="text-sm text-muted-foreground text-center pt-8 border-t">
              <p>Última atualização: Fevereiro, 2024</p>
              <p className="mt-2">
                Para questões relacionadas à privacidade, entre em contato com nosso Encarregado de Proteção de Dados: privacy@simples.health
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 